/**
 * Pure mapping from the `protos.vision.generations.v1` wire types to the legacy generation
 * record consumed by the generation page (`useGenerationData` / `GenerationPage`), so the UI
 * is unaware of the transport. The proto `VisionGeneration` carries server-presigned media
 * URLs, which map onto `generated_path` / `thumbnail_path` (the page's URL resolver passes a
 * full https URL through unchanged). A pending/failed row has empty media URLs.
 */

import { timestampDate } from '@bufbuild/protobuf/wkt';
import { CreditSource } from '../gen/vision/billing/v1/vision_billing_pb';
import { GenerationStatus } from '../gen/vision/generations/v1/vision_generation_pb';
import { fromProtoGradingSettings } from './gradingSettingsMapping.js';
import { regionsToLegacy } from './visionMaskRegionsMapping.js';

const CREDIT_SOURCE_TO_PROTO = {
  project: CreditSource.PROJECT,
  personal: CreditSource.PERSONAL,
};

/** The legacy credit-source string -> the proto `CreditSource` enum (defaults to the project pool). */
export function creditSourceToProto(creditSource) {
  return CREDIT_SOURCE_TO_PROTO[creditSource] ?? CreditSource.PROJECT;
}

function emptyToNull(value) {
  return value ? value : null;
}

function isoOrNull(timestamp) {
  return timestamp ? timestampDate(timestamp).toISOString() : null;
}

const STATUS_TO_LEGACY = {
  [GenerationStatus.PENDING]: 'pending',
  [GenerationStatus.COMPLETE]: 'complete',
  [GenerationStatus.FAILED]: 'failed',
};

/** The proto `GenerationStatus` enum value -> the legacy status string the page branches on. */
export function statusToLegacy(status) {
  return STATUS_TO_LEGACY[status] || 'pending';
}

export function generationLikeToLegacy(like) {
  return {
    user_id: like.accountId,
    display_name: like.name || '',
    avatar_path: like.pfpUrl || null,
    created_at: isoOrNull(like.createdAt),
  };
}

export function generationLikesToLegacy(likes = []) {
  return likes.map(generationLikeToLegacy);
}

/** A proto `VisionGeneration` -> the legacy generation record. */
/**
 * The proto `GenerationCrop` (protobuf-es unwraps `aspect_lock` to `number | undefined`) -> the
 * legacy `crop_params` JSON string the artboard + crop tooling consume, or null when uncropped.
 */
export function cropToLegacy(crop) {
  if (!crop) return null;
  return JSON.stringify({
    x: crop.x,
    y: crop.y,
    width: crop.width,
    height: crop.height,
    aspectLock: crop.aspectLock ?? null,
  });
}

export function cropToProto(cropParams) {
  return {
    x: cropParams.x,
    y: cropParams.y,
    width: cropParams.width,
    height: cropParams.height,
    aspectLock: cropParams.aspectLock == null ? undefined : cropParams.aspectLock,
  };
}

export function generationToLegacy(generation) {
  const workflowDetails = generation.workflowDetails;
  // An upscale rebuilds the legacy `generation_params` the FE affordances branch on (the
  // "Upscaled to 2K" badge, the hidden prompt, the "Before Upscale" split).
  const refine = workflowDetails?.case === 'refine' ? workflowDetails.value : null;
  const upscale = workflowDetails?.case === 'upscale' ? workflowDetails.value : null;
  const recreate = workflowDetails?.case === 'recreate' ? workflowDetails.value : null;
  const standardImage = workflowDetails?.case === 'standardImage' ? workflowDetails.value : null;
  const importSceneLook = workflowDetails?.case === 'importSceneLook' ? workflowDetails.value : null;
  const setDressing = workflowDetails?.case === 'setDressing' ? workflowDetails.value : null;
  const maskInpaint = workflowDetails?.case === 'maskInpaint' ? workflowDetails.value : null;
  const details = standardImage ?? refine ?? upscale ?? recreate ?? importSceneLook ?? setDressing ?? maskInpaint;

  // `generation_params` drives the "Recreated" badge, the chain reference thumbnails, and the "Mode" row.
  const generationParams = upscale
    ? JSON.stringify({
        mode: 'upscale',
        sourceGenerationId: emptyToNull(upscale.parentGenerationId),
      })
    : recreate
    ? JSON.stringify({
        mode: 'recreate',
        additionalRefPaths: generation.additionalReferenceUrls ?? [],
      })
    : importSceneLook
    ? JSON.stringify({
        mode: 'import_scene_look',
        imported_from: {
          imageId: importSceneLook.sourceImageId,
          generationId: importSceneLook.sourceGenerationId,
        },
        ...(importSceneLook.clearScene ? { clearScene: true } : {}),
      })
    : setDressing
    ? JSON.stringify({
        mode: 'set_dressing',
        sourceGenerationId: setDressing.sourceGenerationId,
        ...(setDressing.clearScene ? { clearScene: true } : {}),
      })
    : maskInpaint
    ? JSON.stringify({
        mode: 'inpaint',
        maskRegions: regionsToLegacy(maskInpaint.regions),
      })
    : standardImage?.useThinking || refine?.useThinking
    ? JSON.stringify({ useThinking: true })
    : null;

  return {
    id: generation.generationId,
    image_id: generation.imageId,
    project_id: generation.projectId,
    status: statusToLegacy(generation.status),
    generated_path: emptyToNull(generation.generatedUrl),
    thumbnail_path: emptyToNull(generation.thumbnailUrl),
    graded_path: emptyToNull(generation.gradedUrl),
    graded_thumbnail_path: emptyToNull(generation.gradedThumbnailUrl),
    grading_settings: fromProtoGradingSettings(generation.gradingSettings),
    crop_params: cropToLegacy(generation.crop),
    display_path: emptyToNull(generation.displayUrl),
    prompt: details?.prompt ?? '',
    user_prompt: details?.userPrompt ?? '',
    aspect_ratio: emptyToNull(details?.aspectRatio),
    generated_by: generation.generatedByAccountId,
    display_name: emptyToNull(generation.generatedByName),
    avatar_path: emptyToNull(generation.generatedByPfpUrl),
    generated_at: isoOrNull(generation.createdAt),
    generation_time_ms: generation.generationTimeMs,
    error_message: emptyToNull(generation.errorMessage),
    parent_generation_id: emptyToNull(refine?.parentGenerationId ?? upscale?.parentGenerationId ?? recreate?.parentGenerationId ?? maskInpaint?.parentGenerationId),
    color_ref_path: emptyToNull(generation.colorReferenceUrl),
    additional_ref_path: generation.additionalReferenceUrls ?? [],
    generation_params: generationParams,
    mask_regions: regionsToLegacy(generation.maskRegions),
    likes: generationLikesToLegacy(generation.likes ?? []),
  };
}

/** A proto board `VisionGeneration` (workflowDetails.case === 'visionBoardTextToImage') -> the
 * legacy shape the Vision Board UI consumes. Signed media URLs go in the `_path` fields, which
 * `StorageImage`/`useStorageUrl` short-circuit on `https://` values and pass through unchanged. */
export function boardGenerationToLegacy(generation) {
  const details =
    generation.workflowDetails?.case === 'visionBoardTextToImage'
      ? generation.workflowDetails.value
      : null;

  const generationParams = JSON.stringify({
    source: 'vision_board',
    mode: 'text_to_image',
    ...(details?.useThinking ? { useThinking: true } : {}),
  });

  return {
    id: generation.generationId,
    image_id: generation.imageId || null,
    project_id: generation.projectId,
    status: statusToLegacy(generation.status),
    generated_path: emptyToNull(generation.generatedUrl),
    thumbnail_path: emptyToNull(generation.thumbnailUrl),
    display_path: emptyToNull(generation.displayUrl),
    graded_path: null,
    graded_thumbnail_path: null,
    grading_settings: null,
    crop_params: null,
    prompt: details?.prompt ?? '',
    user_prompt: details?.userPrompt ?? '',
    aspect_ratio: emptyToNull(details?.aspectRatio),
    generated_by: generation.generatedByAccountId,
    display_name: emptyToNull(generation.generatedByName),
    avatar_path: emptyToNull(generation.generatedByPfpUrl),
    generated_at: isoOrNull(generation.createdAt),
    generation_time_ms: generation.generationTimeMs,
    error_message: emptyToNull(generation.errorMessage),
    parent_generation_id: null,
    color_ref_path: null,
    additional_ref_path: generation.additionalReferenceUrls ?? [],
    additional_ref_ids: generation.additionalReferenceAssetIds ?? [],
    user_rules: emptyToNull(details?.userRules),
    generation_params: generationParams,
    mask_regions: [],
    likes: generationLikesToLegacy(generation.likes ?? []),
    source: 'vision_board',
  };
}

/** A `ListVisionBoardGenerationsResponse` -> `{ generations, nextPageToken }` in legacy shape. */
export function listBoardResponseToGenerations(response) {
  const generations = (response.result?.generations ?? []).map(boardGenerationToLegacy);
  const nextPageToken = response.pagination?.nextPageToken || null;

  return { generations, nextPageToken };
}

/** A `ListGenerationsByImageResponse` -> `{ generations, nextPageToken }` in legacy shape. */
export function listResponseToGenerations(response) {
  const generations = (response.result?.generations ?? []).map(generationToLegacy);
  const nextPageToken = response.pagination?.nextPageToken || null;

  return { generations, nextPageToken };
}

export function likesResponseToLegacy(response) {
  return { likes: generationLikesToLegacy(response.result?.likes ?? []) };
}

export function recentPromptsResponseToLegacy(response) {
  return (response.result?.prompts ?? []).map((recent) => ({
    prompt: recent.prompt,
    created_at: isoOrNull(recent.createdAt),
  }));
}
